import { Component, OnInit, OnDestroy } from '@angular/core';
import { NetFacadeService } from '../../services/netfacade.service';
import { NavigationService, NavLocation, NavType } from '../../services/navigation.service';
import { NetViewDto, PrepareNetworkTransferResultDto, NetworkAgentInfo, NetworkTransferResultDto } from 'UvExplorerServer.Dtos.TS/NetViews';
import { Subscription } from 'rxjs/Rx';
import { ShareDto } from 'UvExplorerServer.Dtos.TS/Shares';
import { IdentityService } from 'identity';
import { DateService, MessageService } from 'base';
import { UserDto } from 'UvExplorerServer.Dtos.TS';
import { ViewChild } from '@angular/core';


class Share {
	public name: string;
	public description: string;
	public owner: string;
	public targetName: string;
	public targetType: string;
	public access: string;
	public networkGuid: string;
	public shareGuid: string;

	constructor(s: ShareDto, n: NetViewDto) {
		this.name = n.Name;
		this.description = n.Description;
		this.owner = s.SourceUserName;
		this.targetName = this.shareTargetName(s);
		this.targetType = this.shareTargetType(s);
		this.access = this.capitalize(s.AccessType);
		this.networkGuid = n.Guid;
		this.shareGuid = s.Guid;
	}

	private capitalize(s: string): string {
		if (typeof s !== 'string') return ''
		return s.charAt(0).toUpperCase() + s.slice(1)
	}

	private shareTargetName(share: ShareDto): string {
		if (share.HasTargetUserGuid) {
			return "Me";
		}
		else {
			return share.TargetGroupName;
		}
	}

	private shareTargetType(share: ShareDto): string {
		return (share.HasTargetUserGuid ? "User" : "Group");
	}

}


@Component({
	selector: 'network-list',
	templateUrl: './networklist.component.html',
	styleUrls: ['./networklist.component.scss']
})
export class NetworkListComponent implements OnInit, OnDestroy {

	@ViewChild('detailsOffCanvas') 
	public detailsOffCanvas: any;

	public networks: NetViewDto[];
	public shares: Share[];

	public displayNewNetworkDialog: boolean;
	public displayDeleteNetViewConfirm: boolean;
	public displaySharingDialog: boolean;
	public displayDeleteShareConfirm: boolean;
	public displayUserSelectorDialog: boolean;
	public displayPrepareTransferDialog: boolean;

	private netViewTreeLoadedSub: Subscription;
	private netViewTreeChangedSub: Subscription;
	private netViewTreeUnloadedSub: Subscription;
	private sharesLoadedSub: Subscription;
	private sharesChangedSub: Subscription;
	private sharesUnloadedSub: Subscription;

	constructor(private netFacadeService: NetFacadeService,
		private identityService: IdentityService,
		private navService: NavigationService,
		private dateService: DateService,
		private messageService: MessageService) {

		this.rebuildNetworkList();
		this.rebuildShareList();

		this.displayNewNetworkDialog = false;
		this.displayDeleteNetViewConfirm = false;
		this.displaySharingDialog = false;
		this.displayDeleteShareConfirm = false;
		this.displayUserSelectorDialog = false;
		this.displayPrepareTransferDialog = false;

		this.sharedItemGuid = "";

		this.netViewTreeLoadedSub = this.netFacadeService.onNetViewTreeLoaded.subscribe(() => {
			this.rebuildNetworkList();
		});
		this.netViewTreeChangedSub = this.netFacadeService.onNetViewTreeChanged.subscribe(() => {
			this.rebuildNetworkList();
		});
		this.netViewTreeUnloadedSub = this.netFacadeService.onNetViewTreeUnloaded.subscribe(() => {
			this.rebuildNetworkList();
		});
		this.sharesLoadedSub = this.netFacadeService.onSharesLoaded.subscribe(() => {
			this.rebuildShareList();
		});
		this.sharesChangedSub = this.netFacadeService.onSharesChanged.subscribe(() => {
			this.rebuildShareList();
		});
		this.sharesUnloadedSub = this.netFacadeService.onSharesUnloaded.subscribe(() => {
			this.rebuildShareList();
		});
	}

	public formatDateHm(date: string | Date): string {
		return this.dateService.formatDateHm(date);
	}

	public get hasNetworks(): boolean {
		return (!!this.networks && this.networks.length > 0);
	}

	public get hasShares(): boolean {
		return (!!this.shares && this.shares.length > 0);
	}

	public get isLicenseValid(): boolean {
		//return this.licensingService.isLicenseValid;
		return true;
	}

	public get isObserver(): boolean {
		return this.identityService.isObserver;
	}

	public get isAdmin(): boolean {
		return this.identityService.isAdmin;
	}

	public ngOnInit() {
		return;
	}

	public ngOnDestroy(): void {
		if (this.netViewTreeLoadedSub) {
			this.netViewTreeLoadedSub.unsubscribe();
			this.netViewTreeLoadedSub = undefined;
		}
		if (this.netViewTreeChangedSub) {
			this.netViewTreeChangedSub.unsubscribe();
			this.netViewTreeChangedSub = undefined;
		}
		if (this.netViewTreeUnloadedSub) {
			this.netViewTreeUnloadedSub.unsubscribe();
			this.netViewTreeUnloadedSub = undefined;
		}
		if (this.sharesLoadedSub) {
			this.sharesLoadedSub.unsubscribe();
			this.sharesLoadedSub = undefined;
		}
		if (this.sharesChangedSub) {
			this.sharesChangedSub.unsubscribe();
			this.sharesChangedSub = undefined;
		}
		if (this.sharesUnloadedSub) {
			this.sharesUnloadedSub.unsubscribe();
			this.sharesUnloadedSub = undefined;
		}
	}

	private static readonly TotalVh = 100;
	private static readonly TotalPx = 400;
	private static readonly HeaderHeightPx = 40;
	private static readonly RowHeightPx = 40;

	private get myNetworksCount(): number {
		if (this.networks) {
			return this.networks.length;
		}
		else {
			return 0;
		}
	}

	private get sharedNetworksCount(): number {
		if (this.shares) {
			return this.shares.length;
		}
		else {
			return 0;
		}
	}

	private get scrollHeights(): number[] {

		// totalScrollHeight = 100vh - 50 - 60*2 - 30 - 60*2 - 30 - 50
		// totalScrollHeight = 100vh - 400

		let networksCount = this.myNetworksCount;
		let sharesCount = this.sharedNetworksCount;

		let networksVh = 0;
		let networksPx = 0;
		let sharesVh = 0;
		let sharesPx = 0;

		if (networksCount > 0 && sharesCount > 0) {
			let networksWeight = (networksCount / (networksCount + sharesCount));

			networksVh = Math.round(networksWeight * NetworkListComponent.TotalVh);
			networksPx = Math.round(networksWeight * NetworkListComponent.TotalPx);

			sharesVh = (NetworkListComponent.TotalVh - networksVh);
			sharesPx = (NetworkListComponent.TotalPx - networksPx);
		}
		else if (networksCount > 0) {
			networksVh = NetworkListComponent.TotalVh;
			networksPx = NetworkListComponent.TotalPx;
		}
		else if (sharesCount > 0) {
			sharesVh = NetworkListComponent.TotalVh;
			sharesPx = NetworkListComponent.TotalPx;
		}

		return [networksVh, networksPx, sharesVh, sharesPx];
	}

	public get myNetworksTableScrollHeight(): string {

		let values: number[] = this.scrollHeights;

		let vh = values[0];
		let pixels = values[1];

		let totalTableHeight = (this.myNetworksCount * NetworkListComponent.RowHeightPx +
			NetworkListComponent.HeaderHeightPx);

		let result = `min(calc(${vh}vh - ${pixels}px), ${totalTableHeight}px)`;
		//console.log(result);
		return result;
	}

	public get sharedNetworksTableScrollHeight(): string {

		let values: number[] = this.scrollHeights;

		let vh = values[2];
		let pixels = values[3];

		let totalTableHeight = (this.sharedNetworksCount * NetworkListComponent.RowHeightPx +
			NetworkListComponent.HeaderHeightPx);

		let result = `min(calc(${vh}vh - ${pixels}px), ${totalTableHeight}px)`;
		//console.log(result);
		return result;
	}

	public onNetworkClick(netViewGuid: string): void {
		let newNavLoc = new NavLocation(NavType.Network);
		newNavLoc.netViewGuid = netViewGuid;
		this.navService.selectLocation(newNavLoc);
	}

	public sharedItemGuid: string;
	public sourceUserGuid: string;

	public onSharingClick(event: any, netView: NetViewDto): void {
		event.stopPropagation();
		this.sharedItemGuid = netView.Guid;
		this.sourceUserGuid = netView.UserGuid;
		this.displaySharingDialog = true;
	}

	public xferScope: NetViewDto;
	public xferUserSelectorExceptions: string[];
	public xferTargetUser: UserDto;
	public xferInfo: PrepareNetworkTransferResultDto;

	public onTransferClick(event: any, netView: NetViewDto): void {
		event.stopPropagation();
		this.cancelNetworkTransfer();
		this.xferScope = netView;
		this.xferUserSelectorExceptions = [ netView.UserGuid ];
		this.displayUserSelectorDialog = true;
	}

	public initiateNetworkTransfer(selectedUsers: UserDto[]): void {
		this.xferTargetUser = selectedUsers[0];

		this.netFacadeService.prepareNetworkTransfer(this.xferScope.Guid, this.xferScope.UserGuid, 
														this.xferTargetUser.Guid).subscribe(
			(result: PrepareNetworkTransferResultDto) => {
				if (result.HasNetworkAgents) {
					let error = false;
					let needToPrepare = result.NetworkAgents.Some((netAgent: NetworkAgentInfo) => {
						if (netAgent.HasClientAgentApiKeyUserGuid) {
							return (netAgent.ClientAgentApiKeyUserGuid !== this.xferTargetUser.Guid);						}
						else {
							error = true;
							return false;
						}
					});
					if (!error) {
						if (needToPrepare) {
							this.xferInfo = result;
							this.displayPrepareTransferDialog = true;
						}
						else {
							this.doNetworkTransfer([]);
						}
					}
					else {
						this.messageService.displayErrorMessage("The network's owner could not be changed. Try again later.");
						this.cancelNetworkTransfer();
					}
				}
				else {
					this.doNetworkTransfer([]);
				}
			},
			(error) => {
				this.messageService.displayErrorMessage("The network's owner could not be changed. Try again later.");
				this.cancelNetworkTransfer();
			});
	}

	public cancelNetworkTransfer(): void {
		this.xferScope = null;
		this.xferUserSelectorExceptions = [];
		this.xferTargetUser = null;
		this.xferInfo = null;
	}

	public doNetworkTransfer(netAgents: NetworkAgentInfo[]): void {

		this.netFacadeService.doNetworkTransfer(this.xferScope.Guid, this.xferScope.UserGuid, 
												this.xferTargetUser.Guid,
												netAgents.map(elem => elem.ClientAgentApiKey)).subscribe(
			(result: NetworkTransferResultDto) => {
				if (result.Succeeded) {
					this.messageService.displayInfoMessage("The network's owner has been changed.");
				}
				else {
					this.messageService.displayErrorMessage("The network's owner could not be changed. Try again later.");
				}

				this.cancelNetworkTransfer();
				setTimeout(() => window.location.reload(), 2000);				
			},
			(error) => {
				this.messageService.displayErrorMessage("The network's owner could not be changed. Try again later.");
				this.cancelNetworkTransfer();
				setTimeout(() => window.location.reload(), 2000);				
			});
	}


	private netViewToDeleteGuid: string = "";

	public tryDeleteNetView(event: any, netViewGuid: string): void {
		event.stopPropagation();
		this.netViewToDeleteGuid = netViewGuid;
		this.displayDeleteNetViewConfirm = true;
	}

	public cancelDeleteNetView(): void {
		this.netViewToDeleteGuid = "";
		this.displayDeleteNetViewConfirm = false;
	}

	public deleteNetView(): void {
		this.netFacadeService.deleteNetView(this.netViewToDeleteGuid);
		this.netViewToDeleteGuid = "";
		this.displayDeleteNetViewConfirm = false;
	}

	private shareToDeleteGuid: string = "";

	public tryDeleteShare(event: any, shareGuid: string): void {
		event.stopPropagation();
		this.shareToDeleteGuid = shareGuid;
		this.displayDeleteShareConfirm = true;
	}

	public cancelDeleteShare(): void {
		this.shareToDeleteGuid = "";
		this.displayDeleteShareConfirm = false;
	}

	public deleteShare(): void {
		this.netFacadeService.deleteTargetShare(this.shareToDeleteGuid);
		this.shareToDeleteGuid = "";
		this.displayDeleteShareConfirm = false;
	}

	public onNewNetworkDialogClose(): void {
		setTimeout(() => {
			this.displayNewNetworkDialog = false;
		}, 0);
	}

	private rebuildNetworkList(): void {
		this.networks = [...this.netFacadeService.networks];
		this.networks.sort((a, b) => a.Name.toLowerCase() > b.Name.toLowerCase() ? 1 : -1);
	}

	private rebuildShareList(): void {
		this.shares = [];
		this.netFacadeService.targetShares.forEach((share: ShareDto) => {
			let network: NetViewDto = this.netFacadeService.getTargetNetworkByGuid(share.SharedItemGuid);
			if (!!network) {
				let entry = new Share(share, network);
				this.shares.push(entry);
			}
		});
		this.shares.sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1);
	}

}
